/**
 * Terrain from the ČÚZK DMR 5G ArcGIS ImageServer (spec §5.1): one float32 TIFF per mission
 * bounding box, decoded with geotiff and sampled bilinearly. The service reflects any Origin in
 * its CORS headers (checked 2026-10-06), so the browser calls it directly.
 */
import { fromArrayBuffer } from 'geotiff';
import type { Dtm, LngLat, XY } from '@/planner/types';

export const DEFAULT_IMAGE_SERVER: string =
  import.meta.env.VITE_DTM_IMAGE_SERVER ?? 'https://ags.cuzk.gov.cz/arcgis/rest/services/3D/dmr5g_wm/ImageServer';
export const DTM_ATTRIBUTION = '© ČÚZK';

const R = 6378137;
const PIXEL_M = 2;
const MAX_PX = 2000;
const METRES_PER_DEGREE = 40075017 / 360;
const TIMEOUT_MS = 20_000;

/** Service extent in Web Mercator, from the ImageServer metadata (roughly the Czech Republic). */
export const SERVICE_EXTENT = { xmin: 1328687, ymin: 6145515, xmax: 2110301, ymax: 6684365 };

export interface Bbox {
  west: number;
  south: number;
  east: number;
  north: number;
}
/** A Web Mercator extent in metres. */
export interface Merc {
  xmin: number;
  ymin: number;
  xmax: number;
  ymax: number;
}

/** The decoded grid: row 0 is the north edge, values are metres above sea level, no-data is NaN or out of range. */
export interface DtmRaster {
  data: Float32Array;
  width: number;
  height: number;
  mercator: Merc;
}

/** A sampled DTM that also hands out its grid, so the map can draw it. */
export interface RasterDtm extends Dtm {
  raster: DtmRaster;
}

export function toMercator(lng: number, lat: number): XY {
  return { x: (R * lng * Math.PI) / 180, y: R * Math.log(Math.tan(Math.PI / 4 + (lat * Math.PI) / 360)) };
}

export function fromMercator(x: number, y: number): LngLat {
  return [(x / R) * (180 / Math.PI), (2 * Math.atan(Math.exp(y / R)) - Math.PI / 2) * (180 / Math.PI)];
}

export function bboxOf(points: LngLat[], padM = 50): Bbox {
  const lngs = points.map((p) => p[0]);
  const lats = points.map((p) => p[1]);
  const lat0 = (Math.min(...lats) + Math.max(...lats)) / 2;
  const dLat = padM / METRES_PER_DEGREE;
  const dLng = padM / (METRES_PER_DEGREE * Math.cos((lat0 * Math.PI) / 180));
  return { west: Math.min(...lngs) - dLng, south: Math.min(...lats) - dLat, east: Math.max(...lngs) + dLng, north: Math.max(...lats) + dLat };
}

function snap(v: number, step: number, dir: 'down' | 'up'): number {
  // The 1e-9 keeps grid values that floating point lands just off the grid where they are.
  const k = dir === 'down' ? Math.floor(v / step + 1e-9) : Math.ceil(v / step - 1e-9);
  return Number((k * step).toFixed(10));
}

/** Expand outward to a `step`-degree grid, so small polygon edits reuse the same raster. */
export function snapBbox(b: Bbox, step = 0.01): Bbox {
  return { west: snap(b.west, step, 'down'), south: snap(b.south, step, 'down'), east: snap(b.east, step, 'up'), north: snap(b.north, step, 'up') };
}

/** The raster bbox asked for: the points padded by `padM` metres, snapped to the grid. */
export function dtmBbox(points: LngLat[], padM: number): Bbox {
  return snapBbox(bboxOf(points, padM));
}

export function bboxKey(b: Bbox): string {
  return [b.west, b.south, b.east, b.north].map((v) => v.toFixed(4)).join(',');
}

function mercatorOf(b: Bbox): Merc {
  const sw = toMercator(b.west, b.south);
  const ne = toMercator(b.east, b.north);
  return { xmin: sw.x, ymin: sw.y, xmax: ne.x, ymax: ne.y };
}

export function intersectsService(b: Bbox): boolean {
  const m = mercatorOf(b);
  return m.xmax > SERVICE_EXTENT.xmin && m.xmin < SERVICE_EXTENT.xmax && m.ymax > SERVICE_EXTENT.ymin && m.ymin < SERVICE_EXTENT.ymax;
}

export function exportImageUrl(b: Bbox, base = DEFAULT_IMAGE_SERVER): { url: string; size: [number, number]; mercator: Merc } {
  const m = mercatorOf(b);
  const wantW = Math.ceil((m.xmax - m.xmin) / PIXEL_M);
  const wantH = Math.ceil((m.ymax - m.ymin) / PIXEL_M);
  const scale = Math.max(1, wantW / MAX_PX, wantH / MAX_PX);
  const size: [number, number] = [Math.min(MAX_PX, Math.ceil(wantW / scale)), Math.min(MAX_PX, Math.ceil(wantH / scale))];
  const q = new URLSearchParams({
    bbox: [m.xmin, m.ymin, m.xmax, m.ymax].join(','),
    bboxSR: '3857',
    imageSR: '3857',
    size: size.join(','),
    format: 'tiff',
    pixelType: 'F32',
    interpolation: 'RSP_BilinearInterpolation',
    f: 'image',
  });
  return { url: `${base.replace(/\/$/, '')}/exportImage?${q}`, size, mercator: m };
}

function valid(v: number): boolean {
  return Number.isFinite(v) && v > -1000 && v < 9000;
}

/** Bilinear sampling over a north-up float32 grid whose row 0 is the north edge. */
export function gridDtm(data: Float32Array, width: number, height: number, m: Merc): RasterDtm {
  const px = (m.xmax - m.xmin) / width;
  const py = (m.ymax - m.ymin) / height;
  return {
    raster: { data, width, height, mercator: m },
    sample(lng, lat) {
      const p = toMercator(lng, lat);
      const fx = (p.x - m.xmin) / px - 0.5;
      const fy = (m.ymax - p.y) / py - 0.5;
      if (fx < -0.5 || fy < -0.5 || fx > width - 0.5 || fy > height - 0.5) return null;
      const x0 = Math.max(0, Math.min(width - 1, Math.floor(fx)));
      const y0 = Math.max(0, Math.min(height - 1, Math.floor(fy)));
      const x1 = Math.min(width - 1, x0 + 1);
      const y1 = Math.min(height - 1, y0 + 1);
      const tx = Math.max(0, Math.min(1, fx - x0));
      const ty = Math.max(0, Math.min(1, fy - y0));
      const v00 = data[y0 * width + x0];
      const v10 = data[y0 * width + x1];
      const v01 = data[y1 * width + x0];
      const v11 = data[y1 * width + x1];
      if (![v00, v10, v01, v11].every(valid)) return null;
      return (v00 * (1 - tx) + v10 * tx) * (1 - ty) + (v01 * (1 - tx) + v11 * tx) * ty;
    },
  };
}

export async function decodeDtm(buffer: ArrayBuffer, m: Merc): Promise<RasterDtm> {
  const tiff = await fromArrayBuffer(buffer);
  const image = await tiff.getImage();
  const raster = await image.readRasters({ interleave: true });
  const data = raster instanceof Float32Array ? raster : Float32Array.from(raster as ArrayLike<number>);
  return gridDtm(data, image.getWidth(), image.getHeight(), m);
}

/** `null` outside the service, on any failure (a timeout included), or when the server answers with an error document. */
export async function fetchDtm(b: Bbox, fetchImpl: typeof fetch = fetch): Promise<RasterDtm | null> {
  if (!intersectsService(b)) return null;
  const { url, mercator } = exportImageUrl(b);
  try {
    const r = await fetchImpl(url, { signal: AbortSignal.timeout(TIMEOUT_MS) });
    if (!r.ok || !(r.headers.get('Content-Type') ?? '').includes('tiff')) return null;
    return await decodeDtm(await r.arrayBuffer(), mercator);
  } catch {
    return null;
  }
}
